import type { CSSProperties, MouseEvent as ReactMouseEvent, ReactNode, Ref } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type {
  DataTableColumnGroupingModel,
  DataTableRenderCellParams,
  DataTableRowEventHandler,
  DataTableRowModel,
  DataTableRowSelectionModel,
  DataTableCol,
  DataTableColumn,
  SearchValues,
  TableData
} from '../data-table-v2';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { buttonConfigs } from '@/components/custom/arito/custom-input-table/components/action-button-set/ButtonConfigs';
import type { CopyRowPayload, RepeatRowsPayload, ButtonType } from '@/components/custom/arito/custom-input-table/types';
import { ContextMenuBuilder } from '@/components/custom/arito/custom-input-table/utils/context-menu-builder';
import { ContextMenu, PasteHint } from '@/components/custom/arito/custom-input-table/components';
import { buildDataTableCols, ErpDataTableV2 } from '../data-table-v2';
import { TABLE_THEME } from '@/components/custom/erp/table-theme';
import { InputTableEditingProvider } from './editing-context';
import { AritoIcon } from '@/components/custom/arito/icon';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const DEFAULT_ACTION_ORDER: ButtonType[] = [
  'add',
  'delete',
  'copy',
  'paste',
  'moveUp',
  'moveDown',
  'export',
  'pin',
  'refresh',
  'detail',
  'edit',
  'viewInventory',
  'viewReceipt',
  'downloadExcel',
  'uploadExcel',
  'saveAllocation',
  'watch',
  'deleteAllocation'
];

export type InputTableCol<Row extends DataTableRowModel = DataTableRowModel> = DataTableCol<Row>;
export type InputTableColumn = DataTableColumn;
export type InputTableActionHandler = (clipboardText?: string) => void | Promise<void>;
export type ErpInputTableActions = Partial<Record<ButtonType, InputTableActionHandler>>;
export type {
  ButtonType,
  CopyRowPayload,
  InputTableRowParams,
  RepeatRowsPayload
} from '@/components/custom/arito/custom-input-table/types';
export type { TableSelectedCell } from '@/components/custom/arito/custom-input-table/hooks';
export { ContextMenu, PasteHint, buttonConfigs } from '@/components/custom/arito/custom-input-table/components';
export { default as CellField } from '@/components/custom/arito/custom-input-table/components/cell-field';
export { ContextMenuBuilder } from '@/components/custom/arito/custom-input-table/utils';
export { usePasteConfig, useTableRows } from '@/components/custom/arito/custom-input-table/hooks';
export { useInputTableActions } from './use-input-table-actions';

export interface InputTableActionMenuItem {
  key: string;
  label: string;
  onSelect: () => void;
}

export interface InputTableActionMenu {
  label: string;
  icon?: number;
  items: InputTableActionMenuItem[];
}

export const buildInputTableCols = <Row extends DataTableRowModel>(
  cols: ReadonlyArray<InputTableCol<Row>>
): InputTableColumn[] => buildDataTableCols(cols);

function InputTableActionButton({
  icon,
  title,
  disabled,
  onClick,
  wrapperRef,
  testId
}: {
  icon: number;
  title: string;
  disabled: boolean;
  onClick: () => void;
  wrapperRef?: Ref<HTMLSpanElement>;
  testId?: string;
}) {
  return (
    <span ref={wrapperRef} title={title}>
      <Button
        type='button'
        data-testid={testId}
        aria-label={title}
        variant='ghost'
        size='icon'
        onClick={onClick}
        disabled={disabled}
        className={cn('size-6 rounded-none border border-transparent p-0', TABLE_THEME.actionButton)}
      >
        <AritoIcon icon={icon} />
      </Button>
    </span>
  );
}

export interface ErpInputTableV2Props<T extends DataTableRowModel = DataTableRowModel> {
  rows?: T[];
  value?: T[];
  columns: InputTableColumn[];
  mode?: string;
  className?: string;
  tableClassName?: string;
  height?: CSSProperties['height'];
  actions?: ErpInputTableActions;
  actionsDisabled?: boolean;
  actionOrder?: ButtonType[];
  actionMenu?: InputTableActionMenu;
  actionLoading?: boolean;
  toolbar?: ReactNode;
  actionButtons?: ReactNode;
  pasteTarget?: { id: string; field: string } | null;
  selectedRowId?: string;
  onRowClick?: DataTableRowEventHandler<T>;
  onCellClick?: (params: DataTableRenderCellParams<T>) => void;
  onCellContextMenu?: (event: MouseEvent | ReactMouseEvent, params: DataTableRenderCellParams<T>) => void;
  onRowDoubleClick?: DataTableRowEventHandler<T>;
  checkboxSelection?: boolean;
  selectedRowIds?: DataTableRowSelectionModel;
  onRowSelectionChange?: (selectedIds: string[]) => void;
  totalItems?: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onSearchChange?: (searchValues: SearchValues) => void;
  onChange?: (rows: T[]) => void;
  tableActionButtons?: ButtonType[] | string[];
  loading?: boolean;
  getRowId?: (row: T) => string | undefined;
  enableContextMenu?: boolean;
  onCopyData?: (payload: CopyRowPayload<T>) => void;
  onRepeatForRows?: (payload: RepeatRowsPayload<T>) => void;
  hideHeader?: boolean;
  showIndexColumn?: boolean;
  hideEmptyState?: boolean;
  disableColumnFilters?: boolean;
  indexColumnWidth?: number;
  columnGroupingModel?: DataTableColumnGroupingModel;
  headerHeight?: number;
  columnHeaderHeight?: number;
  rowHeight?: number;
  getRowHeight?: (params: { id: string; row: T; model: T }) => number | null | undefined | 'auto';
  autoHeight?: boolean;
  bottomOffset?: number;
  bottomSelector?: string;
  widthMode?: 'content' | 'stretch';
  scrollMode?: 'auto' | 'vertical';
  disableVirtualization?: boolean;
  forceVirtualization?: boolean;
  virtualizationOverscan?: number;
  virtualizationInitialRows?: number;
}

export function ErpInputTableV2<T extends DataTableRowModel = DataTableRowModel>({
  rows = [],
  value,
  columns,
  mode: _mode,
  className,
  tableClassName,
  height,
  actions,
  actionsDisabled = false,
  actionOrder = DEFAULT_ACTION_ORDER,
  actionMenu,
  actionLoading = false,
  toolbar,
  actionButtons: legacyActionButtons,
  pasteTarget,
  selectedRowId,
  onRowClick,
  onCellClick,
  onCellContextMenu,
  onRowDoubleClick,
  checkboxSelection = false,
  selectedRowIds,
  onRowSelectionChange,
  totalItems,
  currentPage = 0,
  pageSize = 100,
  onPageChange,
  onSearchChange,
  onChange: _onChange,
  tableActionButtons,
  loading = false,
  getRowId,
  enableContextMenu = false,
  onCopyData,
  onRepeatForRows,
  hideHeader = false,
  showIndexColumn = true,
  hideEmptyState = true,
  disableColumnFilters = true,
  indexColumnWidth,
  columnGroupingModel,
  headerHeight,
  columnHeaderHeight,
  rowHeight,
  getRowHeight,
  autoHeight = false,
  bottomOffset = 0,
  bottomSelector,
  widthMode = 'content',
  scrollMode = 'auto',
  disableVirtualization = false,
  forceVirtualization = false,
  virtualizationOverscan,
  virtualizationInitialRows
}: ErpInputTableV2Props<T>) {
  const effectiveRows = value ?? rows;
  const displayLoading = loading && effectiveRows.length > 0;
  const effectiveActionOrder = (tableActionButtons as ButtonType[] | undefined) ?? actionOrder;
  const containerRef = useRef<HTMLDivElement>(null);
  const pasteButtonRef = useRef<HTMLSpanElement | null>(null);
  const [computedHeight, setComputedHeight] = useState<string>();
  const [isPasteMenuOpen, setIsPasteMenuOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    position: { x: number; y: number } | null;
    rowData: T | null;
    columnField: string | null;
  }>({
    position: null,
    rowData: null,
    columnField: null
  });
  const resolvedTableClassName = cn(tableClassName);
  const effectiveColumns = columns;

  const updateHeight = useCallback(() => {
    if (!autoHeight || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    let extraHeight = bottomOffset;

    if (bottomSelector) {
      const bottomEl = document.querySelector(bottomSelector) as HTMLElement | null;
      if (bottomEl?.dataset.aritoBottomBarFlow !== 'scroll') {
        extraHeight += bottomEl?.offsetHeight ?? 0;
      }
    }

    const availableHeight = window.innerHeight - rect.top - extraHeight;
    setComputedHeight(availableHeight > 0 ? `${availableHeight}px` : undefined);
  }, [autoHeight, bottomOffset, bottomSelector]);

  useEffect(() => {
    if (!autoHeight) return;

    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, [autoHeight, updateHeight]);

  useEffect(() => {
    if (!autoHeight || !containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => updateHeight());
    resizeObserver.observe(containerRef.current);

    const raf = requestAnimationFrame(updateHeight);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(updateHeight));

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(raf);
      cancelAnimationFrame(raf2);
    };
  }, [autoHeight, updateHeight]);

  useEffect(() => {
    if (!autoHeight || !bottomSelector) return;

    const bottomEl = document.querySelector(bottomSelector) as HTMLElement | null;

    if (!bottomEl) {
      const raf = requestAnimationFrame(() => requestAnimationFrame(updateHeight));
      return () => cancelAnimationFrame(raf);
    }

    const resizeObserver = new ResizeObserver(() => updateHeight());
    resizeObserver.observe(bottomEl);

    const mutationObserver = new MutationObserver(() => updateHeight());
    mutationObserver.observe(bottomEl, {
      attributes: true,
      childList: true,
      subtree: true
    });

    const raf = requestAnimationFrame(() => requestAnimationFrame(updateHeight));

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [autoHeight, bottomSelector, updateHeight]);

  const handleOpenPasteMenu = useCallback(() => {
    if (actionsDisabled || !pasteTarget || typeof actions?.paste !== 'function') return;
    setIsPasteMenuOpen(true);
  }, [actions, actionsDisabled, pasteTarget]);

  const handleClosePasteMenu = useCallback(() => {
    setIsPasteMenuOpen(false);
  }, []);

  const handlePasteFromClipboard = useCallback(
    (clipboardText: string) => {
      actions?.paste?.(clipboardText);
      handleClosePasteMenu();
    },
    [actions, handleClosePasteMenu]
  );

  const closeContextMenu = useCallback(() => {
    setContextMenu({
      position: null,
      rowData: null,
      columnField: null
    });
  }, []);

  const contextMenuOptions = useMemo(
    () =>
      ContextMenuBuilder.buildOptions({
        contextMenu,
        columns: effectiveColumns,
        getRowId: getRowId as ((row: T) => string | number) | undefined,
        onCopyData,
        onRepeatForRows,
        rowsCount: effectiveRows.length
      }),
    [effectiveColumns, contextMenu, effectiveRows.length, getRowId, onCopyData, onRepeatForRows]
  );

  const handleCellContextMenu = useCallback(
    (event: MouseEvent | ReactMouseEvent, params: DataTableRenderCellParams<any>) => {
      if (!enableContextMenu) {
        onCellContextMenu?.(event, params);
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const pointerEvent = event as MouseEvent;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const menuWidth = 200;
      const menuHeight = 100;
      const adjustedX =
        pointerEvent.clientX + menuWidth > viewportWidth ? pointerEvent.clientX - menuWidth : pointerEvent.clientX;
      const adjustedY =
        pointerEvent.clientY + menuHeight > viewportHeight ? pointerEvent.clientY - menuHeight : pointerEvent.clientY;

      onCellClick?.(params);
      setContextMenu({
        position: { x: adjustedX, y: adjustedY },
        rowData: params.row as T,
        columnField: params.field
      });
    },
    [enableContextMenu, onCellClick, onCellContextMenu]
  );

  const actionButtons = useMemo(() => {
    if (!actions) return null;

    const buttonTypes = effectiveActionOrder.filter(type => typeof actions[type] === 'function');

    if (buttonTypes.length === 0) return null;

    return (
      <div className='flex items-center gap-0.5'>
        {buttonTypes.map(type => {
          const config = buttonConfigs[type] ?? { icon: 0, title: '' };
          const action = actions[type];

          if (!action) return null;

          return (
            <InputTableActionButton
              key={type}
              icon={config.icon}
              title={config.title}
              onClick={type === 'paste' ? handleOpenPasteMenu : () => action()}
              disabled={
                (actionsDisabled && type !== 'export' && type !== 'downloadExcel') || (type === 'paste' && !pasteTarget)
              }
              wrapperRef={type === 'paste' ? pasteButtonRef : undefined}
              testId={`erp-input-table-action-${type}`}
            />
          );
        })}
      </div>
    );
  }, [actions, actionsDisabled, effectiveActionOrder, handleOpenPasteMenu, pasteTarget]);

  const resolvedToolbar =
    actionButtons || actionMenu || actionLoading || legacyActionButtons || toolbar ? (
      <div className={cn('flex items-center justify-start gap-2 border-b px-2 py-1', TABLE_THEME.toolbar)}>
        {actionButtons}
        {actionMenu && actionMenu.items.length > 0 && (
          <>
            {actionButtons && <span className={cn('h-4 w-px', TABLE_THEME.toolbarDivider)} />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  disabled={actionsDisabled}
                  className={cn('h-7 gap-1 rounded-sm px-2 text-xs font-semibold', TABLE_THEME.actionMenuButton)}
                >
                  <AritoIcon icon={actionMenu.icon ?? 139} />
                  {actionMenu.label}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='start' className='min-w-[180px]'>
                {actionMenu.items.map(item => (
                  <DropdownMenuItem key={item.key} onClick={item.onSelect} className='text-xs'>
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
        {actionLoading && <Loader2 className={cn('size-4 animate-spin', TABLE_THEME.actionLoading)} />}
        {legacyActionButtons}
        {toolbar}
      </div>
    ) : undefined;

  const tables = useMemo<TableData[]>(
    () => [
      {
        name: '',
        rows: effectiveRows,
        columns: effectiveColumns,
        checkboxSelection,
        selectionModel: selectedRowIds,
        onSelectionModelChange: nextSelectionModel =>
          onRowSelectionChange?.((nextSelectionModel ?? []).map(value => String(value))),
        disableColumnFilters,
        indexColumnWidth,
        columnGroupingModel,
        headerHeight,
        columnHeaderHeight,
        rowHeight: getRowHeight ? (getRowHeight as any) : rowHeight
      }
    ],
    [
      checkboxSelection,
      columnGroupingModel,
      columnHeaderHeight,
      effectiveColumns,
      disableColumnFilters,
      headerHeight,
      getRowHeight,
      indexColumnWidth,
      rowHeight,
      onRowSelectionChange,
      effectiveRows,
      selectedRowIds
    ]
  );

  return (
    <InputTableEditingProvider>
      <div
        ref={containerRef}
        className={cn(
          'flex min-h-0 flex-col overflow-hidden',
          TABLE_THEME.root,
          height === undefined && 'h-72',
          className
        )}
        style={height !== undefined ? { height } : computedHeight ? { height: computedHeight } : undefined}
      >
        <ErpDataTableV2
          tables={tables}
          pageSize={pageSize}
          onRowClick={onRowClick as DataTableRowEventHandler | undefined}
          onCellClick={onCellClick as ((params: DataTableRenderCellParams<any>) => void) | undefined}
          onCellContextMenu={handleCellContextMenu}
          rowContextMenu={false}
          onRowDoubleClick={onRowDoubleClick as DataTableRowEventHandler | undefined}
          selectedRowId={selectedRowId}
          getRowId={getRowId as ((row: any) => string | undefined) | undefined}
          totalItems={totalItems ?? effectiveRows.length}
          currentPage={currentPage}
          onPageChange={onPageChange}
          onSearchChange={onSearchChange}
          loading={displayLoading}
          toolbar={resolvedToolbar}
          hideHeader={hideHeader}
          showIndexColumn={showIndexColumn}
          hideEmptyState={hideEmptyState}
          disableVirtualization={disableVirtualization}
          forceVirtualization={forceVirtualization}
          virtualizationOverscan={virtualizationOverscan}
          virtualizationInitialRows={virtualizationInitialRows}
          disableCellTruncate
          enableCellKeyboardNavigation
          indexColumnWidth={indexColumnWidth}
          widthMode={widthMode}
          scrollMode={scrollMode}
          className={resolvedTableClassName}
        />

        <PasteHint
          open={isPasteMenuOpen}
          anchorRect={pasteButtonRef.current?.getBoundingClientRect() || null}
          triggerRef={pasteButtonRef}
          onPaste={handlePasteFromClipboard}
          onClose={handleClosePasteMenu}
          placeholder='Dán dữ liệu (Sau khi Ctrl+C từ Excel, Bảng dữ liệu)'
        />

        {enableContextMenu && (
          <ContextMenu options={contextMenuOptions} position={contextMenu.position} onClose={closeContextMenu} />
        )}
      </div>
    </InputTableEditingProvider>
  );
}
